import type { SxProps, Theme } from '@mui/material';

/**
 * Vertical sidebar tab style used by:
 * - /configuracoes (SettingsTabs)
 * - /carteira/{id}/patrimonio (PortfolioPatrimonyTab)
 * - /carteira/{id}/dividendos (PortfolioDividendsTab)
 * - /ativo/{ticker}/caixa (TickerCashTabClient)
 * - /ativo/{ticker}/carteira (TickerPortfolioTabClient)
 *
 * Mirrors the recipe of the Portfolio tabs (the ribbon at the top of
 * /ativo/{ticker}) — see src/components/catalogo/TickerDetailTabs.tsx.
 *
 * - Container: tab ribbon background (hsl(var(--tab-ribbon))), shared by
 *   portfolio, fund and settings ribbons. Light = a slightly desaturated
 *   grey-green tone derived from the landing identity (the landing hero
 *   keeps its own `--page-glow`); dark = subtle green-tinted dark surface.
 * - Header: tab-ribbon-foreground, bold 700.
 * - Inactive item: pearl pill (hsl(var(--card))) + muted-foreground text +
 *   thin grey border. Hover swaps bg to muted.
 * - Active item: solid hsl(var(--secondary)) pastel-green pill, matching the
 *   active top-level tabs across the site.
 */
export const SIDEBAR_TAB_PAPER_SX: SxProps<Theme> = {
  p: 1,
  width: { xs: '100%', md: 260 },
  minWidth: { md: 260 },
  height: 'fit-content',
  borderRadius: 3,
  backgroundColor: 'hsl(var(--tab-ribbon))',
  color: 'hsl(var(--tab-ribbon-foreground))',
  borderColor: 'transparent',
};

export const SIDEBAR_TAB_HEADER_SX: SxProps<Theme> = {
  px: 1,
  letterSpacing: 0.8,
  color: 'hsl(var(--tab-ribbon-foreground))',
  fontWeight: 700,
};

export const sidebarTabButtonSx = (isActive: boolean): SxProps<Theme> => ({
  justifyContent: 'flex-start',
  width: { xs: 'auto', md: '100%' },
  minWidth: { xs: 'max-content', md: 0 },
  flexShrink: { xs: 0, md: 1 },
  minHeight: { xs: 44, md: 36 },
  whiteSpace: { xs: 'nowrap', md: 'normal' },
  textAlign: 'left',
  lineHeight: 1.2,
  borderRadius: 999,
  px: 1.5,
  fontWeight: 700,
  letterSpacing: { xs: 0.1, sm: 0.12 },
  fontSize: { xs: '0.75rem', sm: '0.82rem' },
  // Plain backgroundColor with !important so it always wins over MUI Button's
  // internal background paint, regardless of variant or theme defaults.
  backgroundColor: isActive ? 'hsl(var(--secondary)) !important' : 'hsl(var(--card)) !important',
  color: isActive ? 'hsl(var(--secondary-foreground)) !important' : 'hsl(var(--muted-foreground)) !important',
  border: '1px solid',
  borderColor: isActive ? 'hsl(var(--secondary))' : 'hsl(var(--border) / 0.9)',
  boxShadow: isActive ? '0 7px 16px hsl(var(--secondary) / 0.28)' : 'none',
  '&:hover': {
    backgroundColor: isActive ? 'hsl(var(--secondary)) !important' : 'hsl(var(--muted)) !important',
    color: isActive ? 'hsl(var(--secondary-foreground)) !important' : 'hsl(var(--foreground)) !important',
  },
});
